/**
 * Unauthenticated-Only Route Component
 * Ensures user is NOT authenticated before accessing auth pages
 * Redirects authenticated users to their appropriate dashboard
 */

import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import { computePostLoginRedirect, enrichUserWithProfileRoles } from "@/lib/auth/redirect";
import { Icon3D } from "@/components/ui/icon-3d";

interface RequireUnauthenticatedProps {
  children: JSX.Element;
}

/**
 * Protects auth routes (/login, /register, etc.) from authenticated users
 * If user is already logged in, redirects them to their appropriate dashboard
 */
export default function RequireUnauthenticated({
  children,
}: RequireUnauthenticatedProps) {
  const { user, isLoading } = useSession();
  const [destination, setDestination] = useState<string | null>(null);
  const [enriching, setEnriching] = useState(false);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user || isLoading) return;
    
    let cancelled = false;
    setEnriching(true);
    
    // Prevent rapid redirect loops - if user is authenticated, give them a moment
    // to complete their login flow before redirecting
    redirectTimeoutRef.current = setTimeout(() => {
      enrichUserWithProfileRoles(user).then(enrichedUser => {
        if (cancelled) return;
        const dest = computePostLoginRedirect("/app", enrichedUser);
        setDestination(dest);
        setEnriching(false);
      }).catch((err) => {
        console.error('[RequireUnauthenticated] Enrichment error:', err);
        if (cancelled) return;
        setDestination(computePostLoginRedirect("/app", user));
        setEnriching(false);
      });
    }, 250); // 250ms delay to avoid redirect loop during rapid auth state changes

    return () => { 
      cancelled = true;
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [user, isLoading]);

  // Show loading state while checking authentication or enriching roles
  if (isLoading || enriching) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Icon3D name="sync" size="2xl" className="mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Checking authorization...</p>
        </div>
      </div>
    );
  }

  // User is already authenticated - redirect to appropriate dashboard
  if (user && destination) {
    return <Navigate to={destination} replace />;
  }

  // User is not authenticated - allow access to auth page
  return children;
}
